<template>
  <div class="asset-editor">
    <div class="box">
      <h2>ویرایش {{ kind === 'building' ? 'ساختمان / گروه ساختمان' : 'آسانسور' }}</h2>
      <p v-if="loading" role="status">در حال دریافت اطلاعات…</p>
      <p v-if="error" class="editor-error" role="alert">{{ error }}</p>
      <button v-if="!loading && !record" class="btn" @click="load">تلاش دوباره</button>
      <template v-if="record">
        <p>کد: <b dir="ltr">{{ record.code }}</b> · {{ record.is_deleted ? 'حذف‌شده' : record.is_active ? 'فعال' : 'غیرفعال' }}</p>
        <p>{{ kind === 'elevator' ? 'تغییر ساختمان آسانسور، ارتباط قبلی را با حفظ سابقه پایان می‌دهد.' : 'مشخصات گروه، شهر، نشانی و موقعیت ساختمان قابل ویرایش‌اند.' }} کد سیستمی ثابت می‌ماند.</p>
        <form @submit.prevent="save">
          <fieldset :disabled="busy || loading || record.is_deleted">
            <div class="editor-grid">
              <label v-for="field in fields" :key="field.name" :for="'asset-' + field.name">
                <span>{{ fieldLabel(field) }}</span>
                <select v-if="field.type === 'relation' || field.choices.length" :id="'asset-' + field.name" v-model="draft[field.name]">
                  <option v-if="field.nullable" :value="null">بدون مقدار</option>
                  <option v-for="choice in choices(field)" :key="String(choice.value)" :value="choice.value">{{ choice.label }}</option>
                </select>
                <input v-else-if="field.type === 'boolean'" :id="'asset-' + field.name" v-model="draft[field.name]" type="checkbox">
                <textarea v-else-if="field.type === 'text'" :id="'asset-' + field.name" v-model="draft[field.name]" :maxlength="field.max_length" rows="3" />
                <input v-else :id="'asset-' + field.name" v-model="draft[field.name]" :type="field.type === 'number' ? 'number' : 'text'" step="any" :maxlength="field.max_length">
              </label>
            </div>
            <button type="submit" class="btn accept" :disabled="busy || !changed">{{ busy ? 'در حال ذخیره…' : 'ذخیره تغییرات' }}</button>
          </fieldset>
          <router-link :to="detailUrl">بازگشت به جزئیات</router-link>
        </form>
      </template>
    </div>
  </div>
</template>
<script>
import { elevatorSections, entityLabels } from '@/utils/entityDetails';
import { errorText } from '@/utils/executionAdmin';
const rows = res => Array.isArray(res.data) ? res.data : res.data.results;
const labels = Object.assign(Object.fromEntries(elevatorSections.flatMap(section => section.fields.map(field => [field[0], field[1]]))), {
  name: 'نام داخلی', verbose_name: 'نام نمایشی', type: 'نوع', parent: 'مجتمع / گروه ساختمان', city: 'شهر',
  address: 'نشانی', latitude: 'عرض جغرافیایی', longitude: 'طول جغرافیایی', is_visiting: 'در حال بازدید',
  motor_nameplate_image: 'نشانی تصویر پلاک موتور', control_panel_image: 'نشانی تصویر تابلو فرمان', building: 'ساختمان محل آسانسور',
});
export default {
  data: () => ({ loading: false, busy: false, error: '', record: null, fields: [], draft: {}, initial: {}, relations: {}, sequence: 0 }),
  computed: {
    kind() { return this.$route.meta.recordKind; },
    projectId() { return this.$STORE.state.userConfig.setProjectId; },
    detailUrl() { return `/elevatormanagement/${this.kind}detail/${this.$route.params.id}`; },
    entity() { return this.kind === 'building' ? 'Building' : 'Elevator'; },
    changed() { return Object.keys(this.payload()).length > 0; },
  },
  mounted() { this.load(); },
  beforeDestroy() { this.sequence++; },
  watch: { projectId() { this.load(); }, '$route.params.id'() { this.load(); } },
  methods: {
    fieldLabel(field) { return labels[field.name] || field.label; },
    choices(field) {
      return (this.relations[field.name] || field.choices).map(choice => ({ ...choice, label: entityLabels[choice.label] || choice.label }));
    },
    payload() {
      const data = {};
      this.fields.forEach(field => {
        const value = this.draft[field.name];
        const normalized = value === '' && field.nullable ? null : value;
        const original = this.initial[field.name];
        if (normalized !== original && String(normalized) !== String(original)) data[field.name] = normalized;
      });
      return data;
    },
    async load() {
      const sequence = ++this.sequence, project = this.projectId;
      this.loading = true; this.record = null; this.error = ''; this.fields = []; this.draft = {}; this.relations = {};
      const get = async url => {
        const res = await this.$ApiServiceLayer.get(url, '');
        if (res.status !== 200) throw new Error(errorText(this.$ApiServiceLayer, res));
        return res;
      };
      try {
        const record = (await get(`/api/visit/${this.entity}Edits/${this.$route.params.id}/?p=${project}&edit_schema=true`)).data;
        const requests = this.kind === 'building' ? [
          get(`/api/visit/BuildingListCreate/?p=${project}&type=Complex`),
          get(this.$PATH.SERVICE_NAME.AUTH + this.$PATH.RELATIVE_PATH.GET.GET_CITY_LIST + `?p=${project}`),
        ] : [get(`/api/visit/BuildingListCreate/?p=${project}`)];
        const related = await Promise.all(requests);
        if (sequence !== this.sequence || project !== this.projectId) return;
        const buildingChoices = rows(related[0]).filter(row => row.id !== (this.kind === 'building' ? record.id : null)).map(row => ({ value: row.id, label: (row.verbose_name || row.code) + (row.is_active ? '' : ' (غیرفعال)') }));
        const relations = this.kind === 'building' ? {
          parent: buildingChoices,
          city: rows(related[1]).map(row => ({ value: row.city.id, label: `${row.city.province.name} / ${row.city.name}` })),
        } : { building: buildingChoices };
        const draft = {};
        record.edit_fields.forEach(field => {
          let value = record[field.name];
          if (value && typeof value === 'object') value = value.id;
          draft[field.name] = value == null ? null : value;
          if (field.type === 'relation' && value != null && !(relations[field.name] || []).some(choice => choice.value === value)) {
            relations[field.name].push({ value, label: field.name === 'city' ? record.city.name : 'رکورد فعلی (' + value + ')' });
          }
        });
        this.record = record; this.fields = record.edit_fields; this.draft = draft; this.initial = { ...draft }; this.relations = relations;
      } catch (e) { if (sequence === this.sequence) this.error = e.message || 'دریافت اطلاعات انجام نشد.'; }
      finally { if (sequence === this.sequence) this.loading = false; }
    },
    async save() {
      if (this.busy || !this.changed) return;
      const project = this.projectId, sequence = this.sequence;
      this.busy = true; this.error = '';
      try {
        const res = await this.$ApiServiceLayer.patch(`/api/visit/${this.entity}Edits/${this.record.id}/?p=${project}`, '', this.payload());
        if (sequence !== this.sequence || project !== this.projectId) return;
        if (res.status !== 200) throw new Error(errorText(this.$ApiServiceLayer, res));
        this.$router.push(this.detailUrl);
      } catch (e) { if (sequence === this.sequence) this.error = e.message || 'ذخیره انجام نشد.'; }
      finally { this.busy = false; }
    },
  },
};
</script>
<style scoped>
.asset-editor { padding: 32px; }.box { padding: 24px; background: white; border-radius: 12px; }
.editor-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; margin: 24px 0; }
.editor-grid label { display: flex; flex-direction: column; gap: 8px; }
input, select, textarea { width: 100%; min-height: 44px; border: 1px solid #b9c5d8; border-radius: 8px; padding: 8px; }
input[type=checkbox] { width: 24px; } fieldset { min-width: 0; border: 0; padding: 0; }
.editor-error { color: #a12727; white-space: pre-wrap; } form > a { display: inline-block; margin: 16px; }
@media(max-width: 600px) { .asset-editor { padding: 12px; }.editor-grid { grid-template-columns: 1fr; } }
</style>
